import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ResultsCard } from "../../common/DataList";
import RDOInviteListFilters from "./RDOInviteListFilters";
import useRDOInviteList, { STATUTS_ENVOI } from "./useRDOInviteList";
import tools from "../../../helpers/tools";
import dataService from "../../../helpers/dataService";

const COUNT_LABELS = {
  none: "Aucune invitation trouvée",
  one: "invitation trouvée",
  many: "invitations trouvées",
};

const RDOInviteList = ({ numeroLotInitial }) => {
  const {
    invitations,
    search,
    isLoading,
    nbPages,
    nbResults,
    hasSearched,
    searchInvitations,
    updateSearch,
    resetFilters,
    changePage,
    changeNbItemsByPage,
    setSearchFilters,
  } = useRDOInviteList();

  const [orgs, setOrgs] = useState([]);

  useEffect(() => {
    dataService.get("organizations/orientation_partners", setOrgs);
  }, []);

  // Pré-remplir le filtre et lancer la recherche si numeroLotInitial est fourni
  useEffect(() => {
    if (numeroLotInitial) {
      const newSearch = setSearchFilters({ numeroLot: numeroLotInitial });
      // Lancer la recherche avec le nouvel état
      searchInvitations(false, false, newSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numeroLotInitial]);

  // Fonction pour obtenir le label lisible d'un statut
  const getStatutLabel = (statut) => {
    switch (statut) {
      case STATUTS_ENVOI.A_TRAITER:
        return "À traiter";
      case STATUTS_ENVOI.EN_COURS:
        return "En cours";
      case STATUTS_ENVOI.ENVOYE:
        return "Envoyé";
      case STATUTS_ENVOI.ERREUR:
        return "Erreur";
      default:
        return statut;
    }
  };

  // Configuration des champs du tableau
  const tableFields = [
    {
      name: "nom",
      path: "nom",
      exportPath: "nom",
      hideInTable: true, // Marqueur pour cacher dans le tableau
    },
    {
      name: "prenom",
      path: "prenom",
      exportPath: "prenom",
      hideInTable: true,
    },
    {
      name: "nir",
      path: "nir",
      exportPath: "nir",
      hideInTable: true,
    },
    {
      name: "Allocataire",
      path: "*",
      render: (row) => {
        const link =
          row.id && row.model
            ? tools.getRecipientLinkFromParams(row.model, row.id)
            : null;

        return (
          <div>
            <div className="font-weight-bold">
              {link ? (
                <Link to={link} className="text-primary" target="_blank">
                  {row.nom} {row.prenom}
                </Link>
              ) : (
                <span>
                  {row.nom} {row.prenom}
                </span>
              )}
            </div>
            <small className="text-muted d-block">NIR: {row.nir}</small>
            {row.email && (
              <small className="text-muted d-block">{row.email}</small>
            )}
          </div>
        );
      },
    },
    {
      name: "dateNaissance",
      path: "dateNaissance",
      exportPath: "dateNaissance",
      render: (date) => tools.formatDate(date) || "-",
    },
    {
      name: "Orient. avant relance",
      path: "*",
      exportPath: "orientationAvantRDO",
      render: (line) => {
        if (!line.orientationAvantRDO) return "-";
        return (
          <div>
            <div>{line.orientationAvantRDO}</div>
            {line.dateOrientationAvantRDO && (
              <small className="text-muted d-block" style={{ fontSize: 11 }}>
                {tools.formatDate(line.dateOrientationAvantRDO)}
              </small>
            )}
          </div>
        );
      },
    },
    {
      name: "RDO complété après relance ?",
      path: "dateCompletionRDO",
      render: (date) => {
        if (!date) {
          return <span className="badge badge-secondary">Non</span>;
        }
        return (
          <div>
            <span className="badge badge-success">Oui</span>
            <div className="text-muted" style={{ fontSize: 11 }}>
              le {tools.formatDate(date)}
            </div>
          </div>
        );
      },
    },
    {
      name: "Orient. après relance",
      path: "*",
      exportPath: "orientationApresRDO",
      render: (line) => {
        if (!line.orientationApresRDO) return "-";
        const isMaintien =
          line.orientationAvantRDO &&
          line.orientationAvantRDO === line.orientationApresRDO;
        return (
          <div>
            <div>{line.orientationApresRDO}</div>
            {isMaintien && <span className="badge badge-info">Maintien</span>}
            {line.dateOrientationApresRDO && (
              <small className="text-muted d-block" style={{ fontSize: 11 }}>
                {tools.formatDate(line.dateOrientationApresRDO)}
              </small>
            )}
          </div>
        );
      },
    },
    {
      name: "Date relance",
      path: "dateEnvoi",
      render: (date) => tools.formatDate(date) || "-",
    },
    {
      name: "statut",
      path: "statut",
      exportPath: "statut",
      hideInTable: true,
    },
    {
      name: "messageErreur",
      path: "messageErreur",
      exportPath: "messageErreur",
      hideInTable: true,
    },
    {
      name: "numeroLot",
      path: "numeroLot",
      exportPath: "numeroLot",
      hideInTable: true,
    },
    {
      name: "Statut",
      path: "*",
      render: (row) => {
        let badgeClass = "secondary";
        if (row.statut === STATUTS_ENVOI.A_TRAITER) badgeClass = "info";
        if (row.statut === STATUTS_ENVOI.EN_COURS) badgeClass = "warning";
        if (row.statut === STATUTS_ENVOI.ENVOYE) badgeClass = "success";
        if (row.statut === STATUTS_ENVOI.ERREUR) badgeClass = "danger";

        return (
          <div>
            {row.numeroLot && (
              <small
                className="d-block mb-1"
                style={{
                  whiteSpace: "nowrap",
                  fontSize: 10,
                  fontWeight: "bold",
                }}
              >
                {row.numeroLot}
              </small>
            )}
            <span className={`badge badge-${badgeClass}`}>
              {getStatutLabel(row.statut)}
            </span>
            {row.messageErreur && (
              <div
                className="alert alert-danger mt-2 mb-0 py-1 px-2"
                style={{ fontSize: 11 }}
              >
                {row.messageErreur}
              </div>
            )}
          </div>
        );
      },
    },

    {
      name: "Agent",
      path: "nomAgent",
    },
  ];

  return (
    <div>
      <RDOInviteListFilters
        search={search}
        updateSearch={updateSearch}
        resetFilters={resetFilters}
        orgs={orgs}
        searchInvitations={searchInvitations}
        isLoading={isLoading}
      />

      <ResultsCard
        caption="Liste des invitations RDO"
        rows={invitations}
        fields={tableFields.filter((f) => !f.hideInTable)}
        count={nbResults}
        countLabels={COUNT_LABELS}
        hasSearched={hasSearched}
        isLoading={isLoading}
        page={search.page}
        nbPages={nbPages}
        nbItemsByPage={search.nbItemsByPage}
        onChangePage={changePage}
        onChangeNbItemsByPage={changeNbItemsByPage}
        itemLabel="invitations"
        exportOptions={{
          fetchDatas: "rdo/relances/chercher",
          fetchParams: {
            ...search,
            nbItemsByPage: "*",
          },
          csvDelimiter: ";",
          formatDatasFn: (datas) =>
            datas.map((d) => ({
              ...d,
              dateNaissance: tools.formatDate(d.dateNaissance),
              dateEnvoi: tools.formatDate(d.dateEnvoi),
              dateCompletionRDO: tools.formatDate(d.dateCompletionRDO),
              statut: getStatutLabel(d.statut),
            })),
          excludedFields: ["Allocataire", "Statut"],
          fields: tableFields, // Passer tous les champs pour l'export
          additionalFields: [{ name: "email", path: "email" }],
        }}
      />
    </div>
  );
};

export default RDOInviteList;
